import type {
    QuickStepActionType,
    QuickStepAction,
    QuickStepFolderType,
} from 'owa-quick-step-types';

import { getMailMenuItemShouldShow } from 'owa-mail-filterable-menu-behavior';
import { type MenuItemType } from 'owa-filterable-menu/lib/components/MenuItemType';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { owaComputedFn } from 'owa-computed-fn';
import { getSelectedTableView } from 'owa-mail-list-store';
import { getFolderTable } from 'owa-folders';

export const getIsQuickStepDisabled: (actions: QuickStepAction[]) => boolean = owaComputedFn(
    (actions: QuickStepAction[]): boolean => {
        // If we're in virtual select all mode, disable quick steps. Actions won't apply properly in this case
        const tableView = getSelectedTableView();
        if (!tableView || tableView.isInVirtualSelectAllMode) {
            return true;
        }

        // If any one of our actions is disabled, this quick step should be disabled
        const mailboxInfo = getModuleContextMailboxInfo();
        for (let i = 0; i < actions.length; i++) {
            if (
                !getMailMenuItemShouldShow(getCmdIdFromActionType(actions[i].type), mailboxInfo) ||
                shouldTypeBeDisabled(actions[i])
            ) {
                return true;
            }
        }
        return false;
    }
);

// function exported for tests
export function getCmdIdFromActionType(type: QuickStepActionType): MenuItemType {
    // It's possible the map doesn't contain the type, if so return the bad item type
    return ActionTypeToCmdIdMap.get(type) ?? -1;
}

const ActionTypeToCmdIdMap: Map<QuickStepActionType, MenuItemType> = new Map<
    QuickStepActionType,
    MenuItemType
>([
    ['markAsRead', 59],
    ['markAsUnread', 59],
    ['delete', 50],
    ['softDelete', 12],
    ['moveTo', 30],
    ['copyTo', 30],
    ['category', 6],
    ['clearCategories', 6],
    ['forwardTo', 52],
    ['forwardAsAttachment', 52],
    ['flag', 51],
    ['clearFlags', 51],
    ['markComplete', 51],
    ['retentionPolicy', 4],
    ['pin', 39],
    ['unpin', 75],
    ['ignore', 20],
    ['snooze', 66],
    ['createTask', 11],
    ['replyWithMeeting', 44],
    ['copilotReplyWithMeeting', 87],
]);

const shouldTypeBeDisabled = (action: QuickStepAction): boolean => {
    switch (action.type) {
        case 'moveTo':
            const folder = action.extraInfo as QuickStepFolderType;
            // if 'Always ask for folder' do not check folder
            if (folder?.isAlwaysAsk === true) {
                return false;
            }
            // Folder must exist to enable move action
            const moveToFolderId = folder?.folderId;
            return !getFolderTable().get(moveToFolderId);
        case 'copyTo':
            // Folder must exist to enable copyTo action
            const copyToFolderId = (action.extraInfo as QuickStepFolderType)?.folderId;
            return !getFolderTable().get(copyToFolderId);
        default:
            return false;
    }
};
